import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarClock,
  MapPin,
  Search,
  Star,
  Users,
} from 'lucide-react';
import { Kicker, SmchsFooter, SmchsNav, SmchsStyleBlock } from '@/components/smchs/Shell';
import {
  BookCover,
  CollectionStyle,
  GrainOverlay,
  Reveal,
} from '@/components/smchs/Collection';
import { authorLine, bookRating, searchHref } from '@/components/smchs/collection-lib';
import { bookclubClubsApi, bookclubMeetingsApi } from '@/services/bookclub-api';
import {
  useCatalogOverview,
  usePopularBooks,
  useFeaturedBooks,
  useReviews,
} from '@/hooks/catalog-hooks';
import { TokenManager } from '@/lib/token-manager';
import { useMyLoanSummary } from '@/hooks/student-loans-hooks';
import { useNotifications } from '@/hooks/notifications-hooks';
import type { Book } from '@/services/catalog-api';

/* =====================================================================
 * SMCHS Library: editorial "living collection" home page.
 *
 * The collection leads: live covers in the hero, a drifting shelf band,
 * a featured spread, then the live noticeboard. Every figure comes from
 * the API; auth-gated sections render an explicit signed-out state
 * (see mistakes log [public-page-auth]) or nothing at all.
 *
 * Radius rule for this page: panels/covers rounded-md (6px) with
 * hairline borders, interactive controls rounded-lg, tag chips
 * rounded-full. Mono small-caps are reserved for data metadata
 * (dates, genres, counts); panel titles are sentence case.
 * ===================================================================== */

/* ===================================================================== */

const Index: React.FC = () => {
  return (
    <div className="smchs-home">
      <SmchsStyleBlock />
      <CollectionStyle />
      <SmchsNav />
      <main className="relative">
        <Hero />
        <ShelfMarquee />
        <FeaturedShelf />
        <CollectionBand />
        <LiveBoard />
        <UpcomingEvents />
        <Finale />
      </main>
      <SmchsFooter />
      <GrainOverlay />
    </div>
  );
};

/* =====================================================================
 * HERO: asymmetric split, headline and search left, live cover fan right.
 * ===================================================================== */

/* Books with an uploaded cover go first; the order is otherwise kept. */
const coversFirst = (books: Book[]): Book[] =>
  [...books].sort((a, b) => Number(!!b.cover_image) - Number(!!a.cover_image));

const Hero: React.FC = () => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { data: catalog } = useCatalogOverview();
  const { data: featured } = useFeaturedBooks(5);
  const { data: popular } = usePopularBooks();

  const titleCount = catalog?.counts?.books;
  const genreCount = catalog?.counts?.genres;

  const fanBooks: Book[] = coversFirst(
    featured && featured.length > 0 ? featured : popular?.most_rated ?? [],
  ).slice(0, 3);
  // Suggest searches that are known to return something: short titles from
  // the live collection, not hard-coded books the library may not hold.
  const suggestions = [...new Set(fanBooks.map((b) => b.title).filter((t) => t.length <= 28))];

  return (
    <section className="relative overflow-hidden border-b border-[var(--border)]">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_55%_at_28%_0%,rgba(30,45,92,0.08),transparent_65%)]" />
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-14 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="font-display font-[900] text-[2.6rem] sm:text-[3.4rem] md:text-[4.2rem] lg:text-[4.75rem] leading-[1.1] tracking-[-0.03em] text-[var(--ink)] max-w-[16ch] pb-1"
          >
            Your next book is{' '}
            <span className="font-serif italic font-normal text-[var(--primary)]">
              on the shelf.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
            className="mt-6 max-w-[52ch] text-[1.05rem] leading-[1.55] text-[var(--ink-soft)]"
          >
            {titleCount
              ? `Search ${titleCount.toLocaleString()} titles across ${
                  genreCount ? genreCount.toLocaleString() : 'all'
                } genres, reserve in a tap, and pick it up at the library desk.`
              : 'Search the collection, reserve in a tap, and pick it up at the library desk.'}
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28, ease: 'easeOut' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (q.trim()) navigate(`/catalog?search=${encodeURIComponent(q.trim())}`);
            }}
            className="mt-9 max-w-[620px]"
          >
            <div className="rounded-lg bg-[var(--surface)] border border-[var(--border)] p-1.5 flex items-center gap-1.5 focus-within:border-[var(--gold)] transition-colors shadow-[0_16px_36px_-24px_rgba(20,24,43,0.35)]">
              <Search className="w-5 h-5 ml-3 text-[var(--muted)] shrink-0" strokeWidth={1.75} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search the catalog"
                placeholder="Search by title, author, or ISBN"
                className="flex-1 min-w-0 bg-transparent outline-none px-2 py-3 text-[15px] placeholder:text-[var(--muted)]"
              />
              <button
                type="submit"
                className="shrink-0 h-11 px-5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[14px] font-medium flex items-center gap-2 hover:bg-[var(--primary)] active:translate-y-px transition-all"
              >
                Search <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="mt-3.5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-[var(--muted)] mr-1">
                  Try
                </span>
                {suggestions.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setQ(tag)}
                    className="px-2.5 py-1 rounded-full bg-[var(--surface)] border border-[var(--border)] text-[12px] text-[var(--ink-soft)] hover:border-[var(--gold)] transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </motion.form>
        </div>

        {/* Cover fan: real books from the live collection */}
        {fanBooks.length >= 3 && (
          <div className="hidden lg:block lg:col-span-5 relative h-[420px]">
            {fanBooks.map((b, i) => {
              const poses = [
                'left-2 top-10 -rotate-6 z-[1] w-[190px]',
                'left-[130px] top-0 rotate-0 z-[3] w-[210px]',
                'right-2 top-14 rotate-[5deg] z-[2] w-[190px]',
              ];
              return (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: 'easeOut' }}
                  className={`absolute ${poses[i]}`}
                >
                  <Link to={searchHref(b)} aria-label={`${b.title} by ${authorLine(b)}`}>
                    <BookCover
                      book={b}
                      className="aspect-[2/3] w-full border border-black/10 shadow-[0_28px_48px_-24px_rgba(20,24,43,0.5)] transition-transform duration-300 hover:-translate-y-2"
                    />
                  </Link>
                </motion.div>
              );
            })}
            {/* The shelf itself */}
            <div className="absolute left-0 right-0 top-[360px] h-px bg-[var(--border)]" />
            <div className="absolute left-6 right-6 top-[363px] h-2 bg-[var(--ink)]/5 blur-[3px]" />
          </div>
        )}
      </div>
    </section>
  );
};

/* =====================================================================
 * SHELF MARQUEE: the collection drifting past. Pauses on hover;
 * the shell's reduced-motion rule freezes it entirely.
 * ===================================================================== */

const ShelfMarquee: React.FC = () => {
  const { data: featured } = useFeaturedBooks(5);
  const { data: popular } = usePopularBooks();
  const { data: catalog } = useCatalogOverview();

  const seen = new Set<number>();
  const shelf: Book[] = coversFirst([...(featured ?? []), ...(popular?.most_rated ?? [])]).filter((b) => {
    if (seen.has(b.id)) return false;
    seen.add(b.id);
    return true;
  });

  if (shelf.length < 6) return null;
  const titleCount = catalog?.counts?.books;

  return (
    <section className="border-b border-[var(--border)] bg-[var(--surface-2)]/60 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-7 flex items-baseline justify-between gap-4">
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)]">
          On the shelf
        </span>
        <Link
          to="/catalog"
          className="text-[13px] text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors inline-flex items-center gap-1.5 group"
        >
          {titleCount ? `All ${titleCount.toLocaleString()} titles` : 'All titles'}
          <ArrowUpRight
            className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
            strokeWidth={1.75}
          />
        </Link>
      </div>
      <div className="py-7">
        <div className="shelf-track flex w-max gap-5 px-5">
          {[...shelf, ...shelf].map((b, i) => (
            <Link
              key={`${b.id}-${i}`}
              to={searchHref(b)}
              aria-label={`${b.title} by ${authorLine(b)}`}
              tabIndex={i >= shelf.length ? -1 : 0}
              aria-hidden={i >= shelf.length}
            >
              <BookCover
                book={b}
                className="w-[96px] aspect-[2/3] transition-transform duration-300 hover:-translate-y-1.5"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =====================================================================
 * FEATURED SHELF: one lead book as an editorial spread, the rest as a
 * compact reading list. Whole cards navigate; no dead buttons.
 * ===================================================================== */

const FeaturedShelf: React.FC = () => {
  const { data: books, isLoading } = useFeaturedBooks(5);

  if (isLoading) {
    return (
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-6">
        <Kicker label="Featured" />
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 flex gap-7">
            <div className="w-[230px] aspect-[2/3] rounded-md bg-[var(--surface-2)] animate-pulse shrink-0" />
            <div className="flex-1 space-y-3 pt-4">
              <div className="h-4 w-24 rounded bg-[var(--surface-2)] animate-pulse" />
              <div className="h-9 w-4/5 rounded bg-[var(--surface-2)] animate-pulse" />
              <div className="h-4 w-2/3 rounded bg-[var(--surface-2)] animate-pulse" />
            </div>
          </div>
          <div className="lg:col-span-5 space-y-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded bg-[var(--surface-2)] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!books || books.length === 0) return null;
  const [lead, ...rest] = books;

  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-6">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <Kicker label="Featured" />
          <h2 className="mt-4 font-display font-[900] text-[2.1rem] md:text-[2.75rem] leading-[1.05] tracking-[-0.025em]">
            This month's shelf.
          </h2>
        </div>
      </div>

      <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        {/* Lead spread */}
        <Reveal className="lg:col-span-7">
          <Link to={searchHref(lead)} className="group flex flex-col sm:flex-row gap-7 md:gap-9">
            <BookCover
              book={lead}
              className="w-[200px] md:w-[230px] aspect-[2/3] shrink-0 border border-black/10 shadow-[0_30px_50px_-28px_rgba(20,24,43,0.45)] transition-transform duration-300 group-hover:-translate-y-1.5"
            />
            <div className="min-w-0 sm:pt-2">
              <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--muted)]">
                <span>{lead.genres?.[0]?.name || 'General'}</span>
                {bookRating(lead) && (
                  <span className="inline-flex items-center gap-1 text-[var(--gold-text)]">
                    <Star className="w-3 h-3 fill-current" strokeWidth={0} />
                    {bookRating(lead)}
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-display font-[900] text-[1.9rem] md:text-[2.4rem] leading-[1.02] tracking-[-0.02em] text-[var(--ink)]">
                {lead.title}
              </h3>
              <p className="mt-2 font-serif italic text-[1.15rem] text-[var(--ink-soft)] pb-0.5">
                {authorLine(lead)}
              </p>
              {lead.description && (
                <p className="mt-4 text-[14.5px] leading-[1.6] text-[var(--ink-soft)] line-clamp-3 max-w-[52ch]">
                  {lead.description}
                </p>
              )}
              <span className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-medium text-[var(--ink)] group-hover:text-[var(--primary)] transition-colors">
                View in the catalog
                <ArrowRight
                  className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                  strokeWidth={1.75}
                />
              </span>
            </div>
          </Link>
        </Reveal>

        {/* Reading list */}
        <div className="lg:col-span-5">
          {rest.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.06}>
              <Link
                to={searchHref(b)}
                className="group flex items-center gap-5 py-4 border-b border-[var(--border)] first:border-t lg:first:border-t-0"
              >
                <BookCover book={b} className="w-[52px] aspect-[2/3] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[15.5px] leading-tight truncate group-hover:text-[var(--primary)] transition-colors">
                    {b.title}
                  </div>
                  <div className="mt-1 text-[13px] text-[var(--ink-soft)] truncate">
                    {authorLine(b)}
                  </div>
                </div>
                {bookRating(b) && (
                  <div className="shrink-0 inline-flex items-center gap-1 font-mono text-[12px] text-[var(--gold-text)]">
                    <Star className="w-3 h-3 fill-current" strokeWidth={0} />
                    {bookRating(b)}
                  </div>
                )}
                <ArrowUpRight
                  className="w-4 h-4 text-[var(--muted)] shrink-0 opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                  strokeWidth={1.75}
                />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =====================================================================
 * COLLECTION BAND: the collection in numbers, big ledger numerals.
 * ===================================================================== */

const CollectionBand: React.FC = () => {
  const { data: catalog, isLoading } = useCatalogOverview();

  // No data (API unreachable or empty) → no band. Zeros would be a lie.
  if (!isLoading && !catalog?.counts) return null;

  const stats = [
    { label: 'Titles', value: catalog?.counts?.books },
    { label: 'Authors', value: catalog?.counts?.authors },
    { label: 'Genres', value: catalog?.counts?.genres },
    { label: 'Publishers', value: catalog?.counts?.publishers },
  ];

  return (
    <section className="mt-20 md:mt-28 border-y border-[var(--border)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal
            key={s.label}
            delay={i * 0.05}
            className={`py-9 md:py-11 ${i > 0 ? 'md:border-l md:border-[var(--border)] md:pl-8' : ''} ${
              i % 2 === 1 ? 'border-l border-[var(--border)] pl-6 md:pl-8' : ''
            } ${i > 1 ? 'border-t border-[var(--border)] md:border-t-0' : ''}`}
          >
            {isLoading ? (
              <div className="h-12 w-24 rounded bg-[var(--surface-2)] animate-pulse" />
            ) : (
              <div className="font-display font-[900] text-[2.6rem] md:text-[3.4rem] leading-none tracking-[-0.03em] tabular-nums text-[var(--ink)]">
                {s.value?.toLocaleString() ?? '0'}
              </div>
            )}
            <div className="mt-2.5 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)]">
              {s.label}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* =====================================================================
 * LIVE BOARD: what's happening right now. Public trending always shows;
 * personal panels mount only behind a synchronous token check
 * ([public-page-auth]: their endpoints 401 for guests and api-base
 * hard-redirects to /signin).
 * ===================================================================== */

const LiveBoard: React.FC = () => {
  const signedIn = TokenManager.isTokenValid();
  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-6">
      <Kicker label="Live" />
      <h2 className="mt-4 font-display font-[900] text-[2.1rem] md:text-[2.75rem] leading-[1.05] tracking-[-0.025em] max-w-[22ch]">
        On at the library right now.
      </h2>
      <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-12 gap-5">
        {signedIn ? (
          <>
            <PanelTrending className="md:col-span-7 md:row-span-2" />
            <PanelLoans className="md:col-span-5" />
            <PanelNotifications className="md:col-span-5" />
            <PanelClubs className="md:col-span-5" />
            <PanelReview className="md:col-span-7" />
          </>
        ) : (
          <>
            <PanelTrending className="md:col-span-7" />
            <PanelSignedOut className="md:col-span-5" />
          </>
        )}
      </div>
    </section>
  );
};

const Panel: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => (
  <Reveal className={className}>
    <div className="h-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
      {children}
    </div>
  </Reveal>
);

const PanelTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[14.5px] font-semibold text-[var(--ink)] mb-5">{children}</div>
);

const PanelTrending: React.FC<{ className?: string }> = ({ className }) => {
  const { data: popular, isLoading } = usePopularBooks();
  const trending = popular?.most_rated?.slice(0, 5) || [];

  return (
    <Panel className={className}>
      <PanelTitle>Trending this week</PanelTitle>
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 py-2">
              <div className="w-9 h-[54px] rounded bg-[var(--surface-2)] animate-pulse shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 rounded bg-[var(--surface-2)] animate-pulse w-3/4" />
                <div className="h-3 rounded bg-[var(--surface-2)] animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : trending.length === 0 ? (
        <p className="text-[14px] text-[var(--ink-soft)] py-4">
          Nothing trending yet. Rate a book to get it moving.
        </p>
      ) : (
        <div>
          {trending.map((b, i) => (
            <Link
              key={b.id}
              to={searchHref(b)}
              className="group flex items-center gap-4 py-3 border-b border-[var(--border)] last:border-0"
            >
              <span className="font-mono text-[12px] text-[var(--muted)] w-5 shrink-0 text-right tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </span>
              <BookCover book={b} className="w-9 aspect-[2/3] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14.5px] truncate group-hover:text-[var(--primary)] transition-colors">
                  {b.title}
                </div>
                <div className="text-[12.5px] text-[var(--ink-soft)] truncate">{authorLine(b)}</div>
              </div>
              {bookRating(b) && (
                <div className="shrink-0 inline-flex items-center gap-1 font-mono text-[12px] text-[var(--gold-text)]">
                  <Star className="w-3 h-3 fill-current" strokeWidth={0} />
                  {bookRating(b)}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </Panel>
  );
};

const PanelSignedOut: React.FC<{ className?: string }> = ({ className }) => (
  <Reveal className={className}>
    <div className="h-full rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6 md:p-7 flex flex-col">
      <PanelTitle>Your shelf</PanelTitle>
      <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[36ch]">
        Loans and due dates, club threads, and challenge progress appear here once you sign in
        with your SMCHS account.
      </p>
      <div className="mt-auto pt-6">
        <Link
          to="/signin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--ink)] text-[var(--paper)] text-[13.5px] font-medium hover:bg-[var(--primary)] active:translate-y-px transition-all"
        >
          Sign in <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        </Link>
      </div>
    </div>
  </Reveal>
);

const PanelLoans: React.FC<{ className?: string }> = ({ className }) => {
  const { data: summary, isLoading } = useMyLoanSummary();
  const loans = summary?.loans?.slice(0, 3) || [];

  return (
    <Reveal className={className}>
      <div className="h-full rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6 md:p-7">
        <PanelTitle>Your loans</PanelTitle>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 rounded bg-[var(--surface-2)] animate-pulse" />
            ))}
          </div>
        ) : loans.length === 0 ? (
          <p className="text-[14px] text-[var(--ink-soft)] py-2">No active loans right now.</p>
        ) : (
          <div>
            {loans.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between gap-4 py-3 border-b border-[var(--border)] last:border-0"
              >
                <div className="min-w-0">
                  <div className="font-medium text-[14.5px] truncate">
                    {l.book_copy_info?.book_title || 'Unknown'}
                  </div>
                  <div className="text-[12px] text-[var(--ink-soft)] truncate">
                    {l.book_copy_info?.book_authors?.join(', ') || ''}
                  </div>
                </div>
                <div
                  className={`shrink-0 inline-flex items-center gap-1.5 font-mono text-[12px] ${
                    l.is_overdue ? 'text-[var(--danger)]' : 'text-[var(--ink-soft)]'
                  }`}
                >
                  <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {l.is_overdue
                    ? `${l.days_overdue}d overdue`
                    : `Due ${new Date(l.due_date).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                      })}`}
                </div>
              </div>
            ))}
          </div>
        )}
        <Link
          to="/student"
          className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
        >
          View all loans <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        </Link>
      </div>
    </Reveal>
  );
};

const PanelNotifications: React.FC<{ className?: string }> = ({ className }) => {
  const { data: notifData, isLoading } = useNotifications({ limit: 3 });
  const items = notifData?.results || [];

  return (
    <Panel className={className}>
      <PanelTitle>Notifications</PanelTitle>
      {isLoading ? (
        <div className="space-y-3.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-9 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-[14px] text-[var(--ink-soft)] py-2">All caught up.</p>
      ) : (
        <ul className="space-y-3.5">
          {items.map((n) => (
            <li key={n.id} className="flex gap-3">
              <span
                className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${
                  n.is_read ? 'bg-[var(--border)]' : 'bg-[var(--gold)]'
                }`}
              />
              <div className="min-w-0">
                <div className="text-[14px] leading-[1.45]">{n.title || n.message}</div>
                <div className="text-[11px] text-[var(--muted)] font-mono uppercase tracking-[0.14em] mt-1">
                  {new Date(n.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

const PanelClubs: React.FC<{ className?: string }> = ({ className }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['home', 'bookclub', 'clubs'],
    queryFn: () => bookclubClubsApi.getClubs({ ordering: '-member_count', page_size: 3 }),
    staleTime: 5 * 60 * 1000,
  });
  const clubs = data?.results ?? [];

  return (
    <Panel className={className}>
      <PanelTitle>Book clubs</PanelTitle>
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : clubs.length === 0 ? (
        <p className="text-[14px] text-[var(--ink-soft)] py-2">
          No clubs are running yet. Start one from the book clubs page.
        </p>
      ) : (
        <div>
          {clubs.map((c) => (
            <Link
              key={c.id}
              to="/book-clubs"
              className="group flex items-center justify-between gap-4 py-3 border-b border-[var(--border)] last:border-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[14.5px] truncate group-hover:text-[var(--primary)] transition-colors">
                    {c.name}
                  </span>
                  {c.activity_level === 'high' && (
                    <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold-text)]">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-[12.5px] text-[var(--ink-soft)] truncate">
                  {c.current_book
                    ? `Reading ${c.current_book.title}`
                    : 'No book set yet'}
                </div>
              </div>
              <div className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] text-[var(--ink-soft)]">
                <Users className="w-3.5 h-3.5" strokeWidth={1.75} /> {c.member_count}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Panel>
  );
};

const PanelReview: React.FC<{ className?: string }> = ({ className }) => {
  const { data, isLoading } = useReviews({ ordering: '-rating', page_size: 1, is_approved: true });
  const review = data?.results?.[0];

  return (
    <Reveal className={className}>
      <div className="h-full rounded-md border border-[var(--border)] bg-[var(--gold)]/10 p-6 md:p-7 flex flex-col">
        <PanelTitle>From the reviews</PanelTitle>
        {isLoading ? (
          <div className="space-y-2">
            <div className="h-4 w-full rounded bg-[var(--surface-2)] animate-pulse" />
            <div className="h-4 w-4/5 rounded bg-[var(--surface-2)] animate-pulse" />
          </div>
        ) : !review ? (
          <p className="text-[14px] text-[var(--ink-soft)]">
            No reviews yet. Borrow something and be the first.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-1 mb-3 text-[var(--gold-text)]">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 ${i < (review.rating ?? 0) ? 'fill-current' : 'opacity-25'}`}
                  strokeWidth={0}
                />
              ))}
            </div>
            <blockquote className="font-serif italic text-[1.2rem] leading-[1.45] text-[var(--ink)] line-clamp-3 pb-0.5">
              &ldquo;{review.review_text}&rdquo;
            </blockquote>
            <div className="mt-auto pt-5 text-[13px] text-[var(--ink-soft)]">
              {review.reviewer_name || 'A reader'}, from the catalog reviews
            </div>
          </>
        )}
      </div>
    </Reveal>
  );
};

/* =====================================================================
 * UPCOMING EVENTS: meetings timeline. The endpoint is IsAuthenticated,
 * so this section only exists for signed-in visitors.
 * ===================================================================== */

const UpcomingEvents: React.FC = () => {
  if (!TokenManager.isTokenValid()) return null;
  return <UpcomingEventsContent />;
};

const UpcomingEventsContent: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['home', 'bookclub', 'meetings'],
    queryFn: () => bookclubMeetingsApi.getMeetings({ days: 45, page_size: 4 }),
    staleTime: 5 * 60 * 1000,
  });
  const meetings = data?.results ?? [];

  if (!isLoading && meetings.length === 0) return null;

  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-6">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <h2 className="font-display font-[900] text-[2.1rem] md:text-[2.75rem] leading-[1.05] tracking-[-0.025em]">
          Coming up.
        </h2>
        <Link
          to="/book-clubs"
          className="text-[13px] text-[var(--ink-soft)] hover:text-[var(--ink)] inline-flex items-center gap-1.5 group transition-colors"
        >
          Full calendar
          <ArrowUpRight
            className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
            strokeWidth={1.75}
          />
        </Link>
      </div>

      <div className="mt-10 border-t border-[var(--border)]">
        {isLoading
          ? [1, 2, 3].map((i) => (
              <div key={i} className="py-8 border-b border-[var(--border)]">
                <div className="h-10 w-3/5 rounded bg-[var(--surface-2)] animate-pulse" />
              </div>
            ))
          : meetings.map((m, i) => {
              const d = new Date(m.date);
              return (
                <Reveal key={m.id} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-4 md:gap-6 py-6 md:py-8 items-center border-b border-[var(--border)]">
                    <div className="col-span-12 md:col-span-2 flex md:block items-center gap-3">
                      <div className="font-display font-[900] text-[2.75rem] md:text-[3.4rem] leading-none tracking-[-0.03em] tabular-nums text-[var(--ink)]">
                        {String(d.getDate()).padStart(2, '0')}
                      </div>
                      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--muted)] md:mt-2">
                        {d.toLocaleDateString('en-GB', { month: 'short', weekday: 'short' })}
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      <span className="inline-block text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold-text)] mb-2">
                        {m.club_name}
                      </span>
                      <h3 className="font-display font-bold text-[1.3rem] md:text-[1.5rem] leading-tight tracking-tight">
                        {m.topic}
                      </h3>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:text-right space-y-1.5">
                      <div className="flex items-center gap-1.5 md:justify-end text-[12.5px] text-[var(--ink-soft)]">
                        <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {m.start_time?.slice(0, 5)} to {m.end_time?.slice(0, 5)}
                      </div>
                      <div className="flex items-center gap-1.5 md:justify-end text-[12.5px] text-[var(--muted)]">
                        <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {m.location}
                      </div>
                      <div className="flex items-center gap-1.5 md:justify-end text-[12.5px] text-[var(--ink-soft)]">
                        <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {m.attending_count} attending
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
      </div>
    </section>
  );
};

/* =====================================================================
 * FINALE: one deliberate full-bleed ink block to close the page.
 * ===================================================================== */

const Finale: React.FC = () => (
  <section className="bg-[var(--ink)] text-[var(--paper)]">
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 md:py-28">
      <Reveal>
        <span className="block w-10 h-px bg-[var(--gold)] mb-8" />
        <h2 className="font-display font-[900] text-[2.75rem] md:text-[4.25rem] leading-[1.1] tracking-[-0.03em] max-w-[16ch] pb-1">
          See you at the{' '}
          <span className="font-serif italic font-normal text-[var(--gold)]">library.</span>
        </h2>
        <p className="mt-6 text-[var(--paper)]/70 text-[1.05rem] leading-[1.6] max-w-[46ch]">
          Sign in with your SMCHS account to borrow, reserve, review, and join a book club.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link
            to="/signin"
            className="inline-flex items-center gap-2 bg-[var(--gold)] text-[var(--on-gold)] px-5 py-3 rounded-lg font-semibold text-[14px] hover:brightness-105 active:translate-y-px transition-all"
          >
            Sign in <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
          <Link
            to="/catalog"
            className="inline-flex items-center gap-2 border border-[var(--paper)]/30 hover:border-[var(--paper)]/70 text-[var(--paper)] px-5 py-3 rounded-lg font-medium text-[14px] transition-colors"
          >
            Browse the catalog
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Index;
